import React, { useRef } from 'react';
import { Box, Divider, TextField, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
} from './shared';

const PIECE_TYPES = [
  {
    value: 'breve',
    label: 'Breve',
    description: 'Recomenda-se para gerar quesitos periciais, peças que sejam curtas e não exijam teses jurídicas.',
  },
  {
    value: 'longa',
    label: 'Longa',
    description: 'Recomenda-se para gerar peças que exijam maior desenvolvimento argumentativo e teses jurídicas.',
  },
];

const ManifestacaoProcessualTrabalhistaForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');
  const [selectedPieceType, setSelectedPieceType] = React.useState(values.tipo_peca || 'breve');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader 
        documentType={documentType}
        customTitle="Manifestação Processual (Trabalhista)"
        customDescription="Peça para se manifestar sobre atos, documentos ou decisões em processo trabalhista"
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Tipo da Peça */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Selecione o tipo da peça que deseja: <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <ToggleButtonGroup
            value={selectedPieceType}
            exclusive
            onChange={(_, newValue) => {
              if (newValue) {
                setSelectedPieceType(newValue);
                handleFieldChange('tipo_peca', newValue);
              }
            }}
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              '& .MuiToggleButton-root': {
                flex: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px !important',
                py: 2,
                px: 3,
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'left',
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              },
            }}
          >
            {PIECE_TYPES.map((type) => (
              <ToggleButton key={type.value} value={type.value}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {type.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {type.description}
                </Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* 2. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
          label="Escreva o nome do seu cliente:"
        />

        {/* 3. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Processo na íntegra."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 4. Fatos do Caso (Opcional) */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Conte mais sobre o caso trazendo a realidade dos fatos, se achar necessário: (Opcional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Descreva os fatos relevantes para a manifestação..."
            value={values.fatos_caso || ''}
            onChange={(e) => handleFieldChange('fatos_caso', e.target.value)}
            inputProps={{ maxLength: 6000 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {(values.fatos_caso || '').length} de 6000 caracteres permitidos.
          </Typography>
        </Box>

        {/* 5. Peça a Rebater */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Indique a peça do processo que deseja rebater ou responder: <Typography component="span" color="error.main">*</Typography>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex. Petição inicial feita na data de 21/05/2025"
            value={values.peca_rebater || ''}
            onChange={(e) => handleFieldChange('peca_rebater', e.target.value)}
            helperText="Informe a peça, despacho ou decisão sobre a qual deseja se manifestar"
          />
        </Box>

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
          description="Grave um áudio descrevendo os pontos que deseja abordar na manifestação."
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Na manifestação processual trabalhista, seja claro e objetivo sobre o que está sendo respondido ou esclarecido. Anexe documentos relevantes para fundamentar sua manifestação." />
    </Box>
  );
};

export default ManifestacaoProcessualTrabalhistaForm;

